import { useMemo, useCallback } from "react";
import { Residue } from "@/types/residue/residue.types";
import { FilterOption } from "@/components/ui/table-header/types";

interface UseResidueFiltersProps {
    residues: Residue[];
    searchTerm: string;
    nameFilters: string[];
}

export const useResidueFilters = ({
    residues,
    searchTerm,
    nameFilters,
}: UseResidueFiltersProps) => {
    const filteredResidues = useMemo(() => {
        if (!residues || residues.length === 0) return [];

        return residues.filter((residue) => {
            // Filter by search term
            if (searchTerm && !residue.name?.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Filter by name filters
            if (nameFilters.length > 0 && !nameFilters.includes(residue.name)) {
                return false;
            }

            return true;
        });
    }, [residues, searchTerm, nameFilters]);

    const getUniqueOptions = useCallback(
        (field: keyof Residue): FilterOption[] => {
            if (!residues || residues.length === 0) return [];

            const uniqueValues = new Set<string>();

            residues.forEach((residue) => {
                if (field === "name" && residue.name) {
                    uniqueValues.add(residue.name);
                }
            });

            return Array.from(uniqueValues)
                .sort()
                .map((value) => ({
                    label: value,
                    value: value,
                }));
        },
        [residues]
    );

    return {
        filteredResidues,
        getUniqueOptions,
    };
}; 